import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { EnhancedImageUpload } from "@/components/ui/enhanced-image-upload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  GenieBranding,
  ExtractedColorPalette,
  SignatureStyle,
  DEFAULT_SIGNATURE_STYLE,
  SIGNATURE_LAYOUT_OPTIONS,
} from '@/types/ui/branding';
import { ColorPaletteStrip } from "./ColorPaletteStrip";
import { PresetPalettePicker } from "./PresetPalettePicker";
import { renderSignaturePreviewHtml } from "./signaturePreview";
import { RotateCcw, Lock, Loader2, ChevronDown, Check, AlertTriangle, Wrench, QrCode, Palette, MessageSquare, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LockedOverlay } from "@/components/features";
import { LogoGeneratorService } from "@/services/branding/LogoGeneratorService";
import { extractColorPalette } from "@/utils/image/colorExtraction";
import { useAccountBranding } from "@/hooks/branding/useAccountBranding";
import { cn } from "@/lib/utils";
import { DEFAULT_AVATAR_BACKGROUND_OPACITY } from "@/lib/avatarBackground";

export type BrandingToolModal = "qr" | "webEmbedAppearance" | "webEmbedFullPage";

const MORE_BRANDING_TOOLS: { modal: BrandingToolModal; icon: LucideIcon; title: string; description: string }[] = [
  {
    modal: "webEmbedFullPage",
    icon: Palette,
    title: "Conversation Page Style",
    description: "Style the full-page conversation your visitors see from your link, QR code or page embed",
  },
  {
    modal: "webEmbedAppearance",
    icon: MessageSquare,
    title: "Popup Widget",
    description: "Style the popup chat widget you embed on any website",
  },
  {
    modal: "qr",
    icon: QrCode,
    title: "QR Code",
    description: "Customize and share your genie's scannable QR code",
  },
];

interface BrandingEditorProps {
  branding: GenieBranding;
  onChange: (branding: GenieBranding) => void;
  onSave?: () => void;
  isSaving?: boolean;
  isLocked?: boolean;
  isPreview?: boolean; // Preview mode disables image uploads
  allowLogoDelete?: boolean; // Allow deletion of team branding logo (only for AccountBrandingPage)
  genieName?: string; // Pre-fill logo generator name field
  /** Calls the agents/signature-style self-heal endpoint to resnapshot phoneNumber/liveUrl. Omit to hide the Fix control (e.g. setup mode, preview). */
  onFixSignatureContacts?: () => void;
  isFixingSignatureContacts?: boolean;
  /** Lets the "More" tab hand off to the dedicated QR code / Web Embed editors. Omit to hide the tab (e.g. setup mode, preview). */
  onSwitchModal?: (modal: BrandingToolModal) => void;
  /** Notifies the parent when the active tab changes, e.g. to hide Save/Reset controls on the "More" tab. */
  onActiveTabChange?: (tab: string) => void;
}

export const BrandingEditor = ({
  branding,
  onChange,
  onSave,
  isSaving,
  isLocked = false,
  isPreview = false,
  allowLogoDelete = false,
  genieName,
  onFixSignatureContacts,
  isFixingSignatureContacts = false,
  onSwitchModal,
  onActiveTabChange,
}: BrandingEditorProps) => {
  const navigate = useNavigate();
  const [previewMode, setPreviewMode] = useState<"idle" | "speaking" | "listening">("idle");
  const [extractedColors, setExtractedColors] = useState<ExtractedColorPalette | null>(branding.extractedPalette || null);
  const [logoGeneratorOpen, setLogoGeneratorOpen] = useState(false);

  // Account branding
  const { branding: accountBranding, hasAccountBranding, isLoading: isLoadingAccountBranding } = useAccountBranding();

  // Logo generator state
  const [logoGenName, setLogoGenName] = useState(genieName || "");
  const [logoGenDescription, setLogoGenDescription] = useState("");
  const [logoGenStyle, setLogoGenStyle] = useState("modern");
  const [logoGenIndustry, setLogoGenIndustry] = useState("");
  const [logoGenUseColors, setLogoGenUseColors] = useState(false);
  const [logoGenPrimaryColor, setLogoGenPrimaryColor] = useState(branding.primaryColor || "#4E9CFF");
  const [logoGenSecondaryColor, setLogoGenSecondaryColor] = useState(branding.secondaryColor || "#000000");
  const [isGeneratingLogo, setIsGeneratingLogo] = useState(false);
  const [generatedLogoUrl, setGeneratedLogoUrl] = useState<string | null>(null);

  // Sync extracted colors from branding prop
  useEffect(() => {
    if (branding.extractedPalette) {
      setExtractedColors(branding.extractedPalette);
    }
  }, [branding.extractedPalette]);

  // Sync genieName prop to logo generator input
  useEffect(() => {
    if (genieName && !logoGenName) {
      setLogoGenName(genieName);
    }
  }, [genieName]);

  const handleGenerateLogo = async () => {
    if (!logoGenName.trim()) {
      toast.error("Please enter a name for the logo");
      return;
    }

    setIsGeneratingLogo(true);
    setGeneratedLogoUrl(null);

    try {
      const result = await LogoGeneratorService.generateLogo({
        name: logoGenName.trim(),
        description: logoGenDescription.trim() || undefined,
        style: logoGenStyle,
        industry: logoGenIndustry || undefined,
        primaryColor: logoGenUseColors ? logoGenPrimaryColor : undefined,
        secondaryColor: logoGenUseColors ? logoGenSecondaryColor : undefined,
      });

      setGeneratedLogoUrl(result.image_url);
      toast.success("Logo generated! Click 'Use This Logo' to apply it.");
    } catch (error) {
      console.error("Logo generation failed:", error);
      toast.error(error instanceof Error ? error.message : "Failed to generate logo");
    } finally {
      setIsGeneratingLogo(false);
    }
  };

  const handleUseGeneratedLogo = async () => {
    if (!generatedLogoUrl) return;

    try {
      // Extract colors from the generated logo
      const colors = await extractColorPalette(generatedLogoUrl);
      handleUploadComplete(generatedLogoUrl, colors);
      setGeneratedLogoUrl(null);
    } catch (error) {
      console.warn("Color extraction failed, applying logo without colors:", error);
      handleUploadComplete(generatedLogoUrl, null);
      setGeneratedLogoUrl(null);
    }
  };

  const handleApplyAccountBranding = () => {
    if (!accountBranding) return;
    onChange({ ...branding, ...accountBranding });
    if (accountBranding.extractedPalette) {
      setExtractedColors(accountBranding.extractedPalette);
    }
    toast.success("Account branding applied! Remember to save.");
  };

  const updateBranding = (updates: Partial<GenieBranding>) => {
    onChange({ ...branding, ...updates });
  };

  const signatureStyle = branding.signatureStyle || DEFAULT_SIGNATURE_STYLE;
  const updateSignatureStyle = (updates: Partial<SignatureStyle>) => {
    onChange({ ...branding, signatureStyle: { ...signatureStyle, ...updates } });
  };

  const handleUploadComplete = (url: string, colors: ExtractedColorPalette | null) => {
    // Set extracted colors in local state for display
    if (colors) {
      setExtractedColors(colors);
    }

    // Create a single, complete branding update with both logo URL and extracted colors
    const updatedBranding: Partial<GenieBranding> = {
      logoUrl: url,
      customAvatarUrl: url,
    };

    // If colors were extracted, apply them to ALL branding properties
    if (colors) {
      updatedBranding.primaryColor = colors.suggestedPrimary;
      updatedBranding.secondaryColor = colors.suggestedSecondary;
      updatedBranding.gradientEnabled = true;
      updatedBranding.extractedPalette = colors;

      // Auto-update QR Code colors
      updatedBranding.qrCodeStyle = {
        ...(branding.qrCodeStyle || {}),
        foregroundColor: colors.suggestedPrimary,
        backgroundColor: "#FFFFFF"
      };

      // Auto-update Web Embed header color
      updatedBranding.embedStyle = {
        ...(branding.embedStyle || {}),
        headerColor: colors.suggestedPrimary
      };

      toast.success("Logo uploaded and colors extracted! Applied across all branding.");
    } else {
      toast.success("Logo uploaded successfully!");
    }

    // Single state update with ALL changes (logo + colors)
    updateBranding(updatedBranding);
  };

  const handleColorSelect = (color: string, type: "primary" | "secondary") => {
    if (type === "primary") {
      updateBranding({ primaryColor: color });
    } else {
      updateBranding({ secondaryColor: color, gradientEnabled: true });
    }
    toast.success(`${type === "primary" ? "Primary" : "Secondary"} color updated`);
  };

  const handlePaletteChange = (palette: string[]) => {
    if (!extractedColors) return;
    const updated = { ...extractedColors, palette };
    setExtractedColors(updated);
    updateBranding({ extractedPalette: updated });
  };

  const handleResetToExtractedColors = () => {
    if (extractedColors) {
      updateBranding({
        primaryColor: extractedColors.suggestedPrimary,
        secondaryColor: extractedColors.suggestedSecondary,
        gradientEnabled: true
      });
      toast.success("Colors reset to extracted palette");
    }
  };

  const handlePresetPaletteSelect = (primary: string, secondary: string) => {
    updateBranding({ primaryColor: primary, secondaryColor: secondary, gradientEnabled: true });
    toast.success("Brand colors updated");
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <Tabs defaultValue="images" className="w-full" onValueChange={onActiveTabChange}>
        <TabsList className={cn("grid w-full h-auto p-1", onSwitchModal ? "grid-cols-4" : "grid-cols-3")}>
          <TabsTrigger value="images" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Images
          </TabsTrigger>
          <TabsTrigger value="colors" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Colors
          </TabsTrigger>
          <TabsTrigger value="signature" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Email Signature
          </TabsTrigger>
          {onSwitchModal && (
            <TabsTrigger value="more" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
              More
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="images" className="space-y-4">
          <Card className="relative">
            {isLocked && <LockedOverlay feature="BRANDING" />}
            <CardHeader className="sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:space-y-0">
              <div>
                <CardTitle>Brand Logo</CardTitle>
                <CardDescription>Upload your company logo or brand image</CardDescription>
              </div>
              {!isPreview && hasAccountBranding && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleApplyAccountBranding}
                  className="w-full sm:w-auto sm:shrink-0 max-w-full justify-start h-auto py-3 whitespace-normal"
                >
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-semibold">Apply Account Branding</p>
                    <p className="text-xs text-muted-foreground font-normal">
                      Use your account's default logo, colors, and styling
                    </p>
                  </div>
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Your Brand Logo</Label>
                <div className="mt-2 mb-3">
                  <p className="text-sm text-gray-600">
                    Your company logo or brand image. This will be used everywhere:
                  </p>
                  <ul className="text-sm text-gray-600 ml-5 mt-1 space-y-0.5 list-disc">
                    <li>As your avatar throughout the app</li>
                    <li>In QR codes (center logo)</li>
                    <li>In web embed widgets</li>
                    <li>In all marketing materials</li>
                  </ul>
                  <p className="text-sm text-blue-600 font-medium mt-2">
                    Colors will be automatically extracted and applied to all branding elements
                  </p>
                </div>
                {isPreview ? (
                  <div className="mt-4 p-8 bg-gray-50 rounded-lg border-2 border-gray-200 flex items-center justify-center">
                    <div className="text-center">
                      <GenieAvatar
                        color={branding.primaryColor}
                        secondaryColor={branding.secondaryColor}
                        gradientEnabled={branding.gradientEnabled}
                        gradientAngle={branding.gradientAngle}
                        avatarBackgroundOpacity={branding.avatarBackgroundOpacity}
                        customAvatarUrl={branding.logoUrl || branding.customAvatarUrl}
                        hasCustomBranding={true}
                        size={120}
                      />
                      <p className="text-sm text-gray-600 mt-4">
                        This is an example logo. Image uploads are disabled in preview mode.
                      </p>
                    </div>
                  </div>
                ) : (
                  <EnhancedImageUpload
                    value={branding.logoUrl || branding.customAvatarUrl}
                    onChange={(url) => {
                      // Fallback for when onUploadComplete is not called (e.g., on remove)
                      updateBranding({ logoUrl: url, customAvatarUrl: url });
                    }}
                    onUploadComplete={handleUploadComplete}
                    onBeforeRemove={(url) => {
                      // Allow deletion only if explicitly enabled (AccountBrandingPage)
                      if (allowLogoDelete) return true;
                      // Block deletion if this is the team branding logo
                      if (accountBranding?.logoUrl && url === accountBranding.logoUrl) return false;
                      if (accountBranding?.customAvatarUrl && url === accountBranding.customAvatarUrl) return false;
                      return true;
                    }}
                    bucket="genie-images"
                    showColorExtraction={true}
                    showBackgroundRemoval={true}
                    squarePad={true}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Logo Generator - Collapsible */}
          {!isPreview && (
            <Collapsible open={logoGeneratorOpen} onOpenChange={setLogoGeneratorOpen}>
              <Card className="relative">
                {isLocked && <LockedOverlay feature="BRANDING" />}
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div>
                          <CardTitle>Generate a Logo</CardTitle>
                          <CardDescription>
                            Quickly generate a logo icon using AI
                          </CardDescription>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${logoGeneratorOpen ? "rotate-180" : ""}`} />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="logoGenName">Name</Label>
                        <Input
                          id="logoGenName"
                          value={logoGenName}
                          onChange={(e) => setLogoGenName(e.target.value)}
                          placeholder="e.g., Plumbing Pros"
                          disabled={isGeneratingLogo}
                        />
                      </div>
                      <div>
                        <Label htmlFor="logoGenStyle">Style</Label>
                        <Select value={logoGenStyle} onValueChange={setLogoGenStyle} disabled={isGeneratingLogo}>
                          <SelectTrigger id="logoGenStyle">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="modern">Modern (Default)</SelectItem>
                            <SelectItem value="bold">Bold</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                            <SelectItem value="tech">Tech</SelectItem>
                            <SelectItem value="luxury">Luxury</SelectItem>
                            <SelectItem value="classic">Classic</SelectItem>
                            <SelectItem value="sticker">Sticker</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="logoGenDescription">Description (optional)</Label>
                      <Textarea
                        id="logoGenDescription"
                        value={logoGenDescription}
                        onChange={(e) => setLogoGenDescription(e.target.value)}
                        placeholder="e.g., A plumbing business in Austin, TX specializing in residential repairs"
                        rows={2}
                        disabled={isGeneratingLogo}
                      />
                    </div>

                    <div>
                      <Label htmlFor="logoGenIndustry">Industry (optional)</Label>
                      <Select value={logoGenIndustry || "none"} onValueChange={(v) => setLogoGenIndustry(v === "none" ? "" : v)} disabled={isGeneratingLogo}>
                        <SelectTrigger id="logoGenIndustry">
                          <SelectValue placeholder="Select industry..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="automotive">Automotive</SelectItem>
                          <SelectItem value="marine">Marine</SelectItem>
                          <SelectItem value="appliances">Appliances</SelectItem>
                          <SelectItem value="office-equipment">Office Equipment</SelectItem>
                          <SelectItem value="home-builders">Home Builders</SelectItem>
                          <SelectItem value="travel-hospitality">Travel & Hospitality</SelectItem>
                          <SelectItem value="manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="industrial">Industrial</SelectItem>
                          <SelectItem value="trades">Trades</SelectItem>
                          <SelectItem value="plumbing">Plumbing</SelectItem>
                          <SelectItem value="electrical">Electrical</SelectItem>
                          <SelectItem value="hvac">HVAC</SelectItem>
                          <SelectItem value="landscaping">Landscaping</SelectItem>
                          <SelectItem value="real-estate">Real Estate</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="fitness">Fitness</SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="retail">Retail</SelectItem>
                          <SelectItem value="technology">Technology</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                          <SelectItem value="legal">Legal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="logoGenUseColors"
                          checked={logoGenUseColors}
                          onCheckedChange={setLogoGenUseColors}
                          disabled={isGeneratingLogo}
                        />
                        <Label htmlFor="logoGenUseColors">Restrict logo to specific colors</Label>
                      </div>
                      {logoGenUseColors && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                          <div>
                            <Label htmlFor="logoGenPrimaryColor">Primary Color</Label>
                            <div className="flex gap-3 mt-2">
                              <div className="relative">
                                <Input
                                  id="logoGenPrimaryColor"
                                  type="color"
                                  value={logoGenPrimaryColor}
                                  onChange={(e) => setLogoGenPrimaryColor(e.target.value)}
                                  className="w-12 h-12 p-1 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                                  disabled={isGeneratingLogo}
                                />
                                <div
                                  className="absolute inset-1 rounded-md border border-white/20 pointer-events-none"
                                  style={{ backgroundColor: logoGenPrimaryColor }}
                                />
                              </div>
                              <Input
                                type="text"
                                value={logoGenPrimaryColor}
                                onChange={(e) => setLogoGenPrimaryColor(e.target.value)}
                                placeholder="#4E9CFF"
                                className="flex-1"
                                disabled={isGeneratingLogo}
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="logoGenSecondaryColor">Secondary Color</Label>
                            <div className="flex gap-3 mt-2">
                              <div className="relative">
                                <Input
                                  id="logoGenSecondaryColor"
                                  type="color"
                                  value={logoGenSecondaryColor}
                                  onChange={(e) => setLogoGenSecondaryColor(e.target.value)}
                                  className="w-12 h-12 p-1 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                                  disabled={isGeneratingLogo}
                                />
                                <div
                                  className="absolute inset-1 rounded-md border border-white/20 pointer-events-none"
                                  style={{ backgroundColor: logoGenSecondaryColor }}
                                />
                              </div>
                              <Input
                                type="text"
                                value={logoGenSecondaryColor}
                                onChange={(e) => setLogoGenSecondaryColor(e.target.value)}
                                placeholder="#000000"
                                className="flex-1"
                                disabled={isGeneratingLogo}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={handleGenerateLogo}
                      disabled={isGeneratingLogo || !logoGenName.trim()}
                      className="w-full sm:w-auto"
                    >
                      {isGeneratingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        "Generate Logo"
                      )}
                    </Button>

                    {/* Generated logo preview */}
                    {generatedLogoUrl && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border-2 border-primary/20">
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                          <img
                            src={generatedLogoUrl}
                            alt="Generated logo"
                            className="w-32 h-32 object-contain rounded-lg bg-white border"
                          />
                          <div className="flex flex-col gap-2 text-center sm:text-left">
                            <p className="text-sm text-gray-600">
                              Logo generated! Apply it to your branding or generate a new one.
                            </p>
                            <div className="flex gap-2 justify-center sm:justify-start">
                              <Button onClick={handleUseGeneratedLogo} size="sm">
                                Use This Logo
                              </Button>
                              <Button
                                onClick={handleGenerateLogo}
                                variant="outline"
                                size="sm"
                                disabled={isGeneratingLogo}
                              >
                                {isGeneratingLogo ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  "Regenerate"
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </TabsContent>

        <TabsContent value="colors" className="space-y-4">
          <Card className="relative">
            {isLocked && <LockedOverlay feature="BRANDING" />}
            <CardHeader className="sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:space-y-0">
              <div>
                <CardTitle>Brand Colors</CardTitle>
                <CardDescription>
                  Customize the color scheme for your genie. Primary color affects the genie avatar, buttons, and highlights throughout the
                  app.
                </CardDescription>
              </div>
              {extractedColors && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetToExtractedColors}
                  className="w-full sm:w-auto sm:shrink-0 flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Extracted
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {extractedColors ? (
                <div className="space-y-3">
                  <ColorPaletteStrip
                    extractedColors={extractedColors}
                    onColorSelect={(color) => updateBranding({ primaryColor: color })}
                    selectedColor={branding.primaryColor}
                    label="Click a color to set as primary"
                    onPaletteChange={handlePaletteChange}
                  />
                  <ColorPaletteStrip
                    extractedColors={extractedColors}
                    onColorSelect={(color) => updateBranding({ secondaryColor: color, gradientEnabled: true })}
                    selectedColor={branding.secondaryColor || undefined}
                    label="Click a color to set as gradient color"
                    onPaletteChange={handlePaletteChange}
                  />
                  <ColorPaletteStrip
                    extractedColors={extractedColors}
                    onColorSelect={(color) => updateBranding({ accentColor: color })}
                    selectedColor={branding.accentColor || undefined}
                    label="Click a color to set as accent"
                    onPaletteChange={handlePaletteChange}
                  />
                </div>
              ) : branding.logoUrl ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    ⏳ Extracting colors from your logo...
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Upload a brand logo in the <strong>Images</strong> tab to automatically extract and apply a color palette!
                  </p>
                </div>
              )}

              <PresetPalettePicker
                onSelect={handlePresetPaletteSelect}
                currentPrimary={branding.primaryColor}
                currentSecondary={branding.secondaryColor || undefined}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-3 mt-2">
                    <div className="relative">
                      <Input
                        id="primaryColor"
                        type="color"
                        value={branding.primaryColor}
                        onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                        className="w-12 h-12 p-1 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                      />
                      <div
                        className="absolute inset-1 rounded-md border border-white/20 pointer-events-none"
                        style={{ backgroundColor: branding.primaryColor }}
                      />
                    </div>
                    <Input
                      type="text"
                      value={branding.primaryColor}
                      onChange={(e) => updateBranding({ primaryColor: e.target.value })}
                      placeholder="#4E9CFF"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="secondaryColor">Gradient Color (Pairs With Primary)</Label>
                  <p className="text-xs text-gray-500 mb-1">The second stop in the avatar/button gradient</p>
                  <div className="flex gap-3 mt-2">
                    <div className="relative">
                      <Input
                        id="secondaryColor"
                        type="color"
                        value={branding.secondaryColor || "#000000"}
                        onChange={(e) => updateBranding({ secondaryColor: e.target.value })}
                        className="w-12 h-12 p-1 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!branding.gradientEnabled}
                      />
                      <div
                        className="absolute inset-1 rounded-md border border-white/20 pointer-events-none"
                        style={{ backgroundColor: branding.secondaryColor || "#000000", opacity: branding.gradientEnabled ? 1 : 0.5 }}
                      />
                    </div>
                    <Input
                      type="text"
                      value={branding.secondaryColor || ""}
                      onChange={(e) => updateBranding({ secondaryColor: e.target.value || null })}
                      placeholder="#000000"
                      className="flex-1"
                      disabled={!branding.gradientEnabled}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="accentColor">Accent Color</Label>
                  <p className="text-xs text-gray-500 mb-1">Used for highlights, links, and secondary UI accents</p>
                  <div className="flex gap-3 mt-2">
                    <div className="relative">
                      <Input
                        id="accentColor"
                        type="color"
                        value={branding.accentColor || "#000000"}
                        onChange={(e) => updateBranding({ accentColor: e.target.value })}
                        className="w-12 h-12 p-1 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors cursor-pointer"
                      />
                      <div
                        className="absolute inset-1 rounded-md border border-white/20 pointer-events-none"
                        style={{ backgroundColor: branding.accentColor || "#000000" }}
                      />
                    </div>
                    <Input
                      type="text"
                      value={branding.accentColor || ""}
                      onChange={(e) => updateBranding({ accentColor: e.target.value || null })}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-4">
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base">Gradient Options</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="gradientEnabled">Enable Gradient</Label>
                        <p className="text-sm text-gray-500">Use a color gradient for the genie</p>
                      </div>
                      <Switch
                        id="gradientEnabled"
                        checked={branding.gradientEnabled || false}
                        onCheckedChange={(checked) => updateBranding({ gradientEnabled: checked })}
                      />
                    </div>

                    {branding.gradientEnabled && (
                      <div>
                        <Label htmlFor="gradientAngle">Gradient Angle</Label>
                        <div className="flex items-center gap-4 mt-2">
                          <Input
                            id="gradientAngle"
                            type="range"
                            min="0"
                            max="360"
                            value={branding.gradientAngle || 135}
                            onChange={(e) => updateBranding({ gradientAngle: parseInt(e.target.value) })}
                            className="flex-1"
                          />
                          <span className="text-sm font-mono w-12">{branding.gradientAngle || 135}°</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base">Avatar Background</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="avatarBackgroundSolid">Full Solid Color</Label>
                        <p className="text-sm text-gray-500">Fill the avatar background with the solid primary color instead of a light tint</p>
                      </div>
                      <Switch
                        id="avatarBackgroundSolid"
                        checked={branding.avatarBackgroundOpacity === 100}
                        onCheckedChange={(checked) => updateBranding({ avatarBackgroundOpacity: checked ? 100 : null })}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="avatarBackgroundOpacity">Color Opacity</Label>
                        {branding.avatarBackgroundOpacity != null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => updateBranding({ avatarBackgroundOpacity: null })}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Default
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <Input
                          id="avatarBackgroundOpacity"
                          type="range"
                          min="0"
                          max="100"
                          value={branding.avatarBackgroundOpacity ?? DEFAULT_AVATAR_BACKGROUND_OPACITY}
                          onChange={(e) => updateBranding({ avatarBackgroundOpacity: parseInt(e.target.value) })}
                          className="flex-1"
                        />
                        <span className="text-sm font-mono w-12">
                          {branding.avatarBackgroundOpacity ?? DEFAULT_AVATAR_BACKGROUND_OPACITY}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-4 sm:mt-6">
                <Label>Preview</Label>
                <div className="mt-2 p-4 sm:p-8 bg-gray-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-40 h-40 sm:w-52 sm:h-52 mx-auto">
                      <GenieAvatar
                        color={branding.primaryColor}
                        secondaryColor={branding.secondaryColor}
                        gradientEnabled={branding.gradientEnabled}
                        gradientAngle={branding.gradientAngle}
                        avatarBackgroundOpacity={branding.avatarBackgroundOpacity}
                        speaking={previewMode === "speaking"}
                        listening={previewMode === "listening"}
                        size={160}
                        customAvatarUrl={branding.logoUrl || branding.customAvatarUrl}
                        hasCustomBranding={true}
                      />
                    </div>
                    <div className="mt-4 flex gap-2 justify-center">
                      <Button variant={previewMode === "idle" ? "default" : "outline"} size="sm" onClick={() => setPreviewMode("idle")}>
                        Idle
                      </Button>
                      <Button
                        variant={previewMode === "speaking" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPreviewMode("speaking")}
                      >
                        Speaking
                      </Button>
                      <Button
                        variant={previewMode === "listening" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPreviewMode("listening")}
                      >
                        Listening
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signature" className="space-y-4">
          <Card className="relative">
            {isLocked && <LockedOverlay feature="BRANDING" />}
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Email Signature</CardTitle>
                  <CardDescription>
                    Controls the branded signature Help Genie Mail appends to this genie's email replies.
                  </CardDescription>
                </div>
                <Switch
                  checked={signatureStyle.enabled}
                  onCheckedChange={(checked) => updateSignatureStyle({ enabled: checked })}
                />
              </div>
            </CardHeader>
            {signatureStyle.enabled && (
              <CardContent className="space-y-6">
                {(!signatureStyle.liveUrl || !signatureStyle.phoneNumber) && (
                  <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-900">
                        <p className="font-medium">
                          {!signatureStyle.liveUrl && !signatureStyle.phoneNumber
                            ? "No live link or phone number saved for this signature"
                            : !signatureStyle.liveUrl
                              ? "No live link saved for this signature"
                              : "No phone number saved for this signature"}
                        </p>
                        <p className="text-amber-700 mt-0.5">
                          The QR code and contact badges won't render in emails until this is fixed.
                        </p>
                      </div>
                    </div>
                    {onFixSignatureContacts && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={onFixSignatureContacts}
                        disabled={isFixingSignatureContacts}
                      >
                        {isFixingSignatureContacts ? (
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Wrench className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Fix
                      </Button>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-base font-semibold">Layout</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                    {SIGNATURE_LAYOUT_OPTIONS.map((option) => {
                      const isSelected = signatureStyle.layout === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updateSignatureStyle({ layout: option.value })}
                          className={cn(
                            "relative text-left rounded-xl px-4 py-3 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2",
                            isSelected
                              ? "ring-2 ring-genie-primary focus:ring-genie-primary bg-genie-primary/5"
                              : "ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-gray-300 bg-white",
                          )}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-genie-primary">
                              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                            </span>
                          )}
                          <p className={cn("font-medium text-sm", isSelected ? "text-genie-primary" : "text-gray-900")}>
                            {option.label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-base font-semibold">Sections</Label>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="sigShowDescription">Description line</Label>
                      <p className="text-sm text-gray-500">Shows the genie's description under its name</p>
                    </div>
                    <Switch
                      id="sigShowDescription"
                      checked={signatureStyle.showDescription}
                      onCheckedChange={(checked) => updateSignatureStyle({ showDescription: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="sigShowChannelBadges">Contact badges</Label>
                      <p className="text-sm text-gray-500">Chat, call, email, and web links as pill badges</p>
                    </div>
                    <Switch
                      id="sigShowChannelBadges"
                      checked={signatureStyle.showChannelBadges}
                      onCheckedChange={(checked) => updateSignatureStyle({ showChannelBadges: checked })}
                    />
                  </div>

                  <div className={cn("flex items-center justify-between", signatureStyle.layout === "compact" && "opacity-50")}>
                    <div>
                      <Label htmlFor="sigShowQrCode">QR code</Label>
                      <p className="text-sm text-gray-500">"Scan to chat" QR code for the genie's live page</p>
                    </div>
                    <Switch
                      id="sigShowQrCode"
                      checked={signatureStyle.showQrCode}
                      disabled={signatureStyle.layout === "compact"}
                      onCheckedChange={(checked) => updateSignatureStyle({ showQrCode: checked })}
                    />
                  </div>

                  <div className={cn("flex items-center justify-between", signatureStyle.layout === "compact" && "opacity-50")}>
                    <div>
                      <Label htmlFor="sigShowMascot">Help Genie mascot</Label>
                      <p className="text-sm text-gray-500">Small mascot artwork beside the QR code</p>
                    </div>
                    <Switch
                      id="sigShowMascot"
                      checked={signatureStyle.showMascot}
                      disabled={signatureStyle.layout === "compact"}
                      onCheckedChange={(checked) => updateSignatureStyle({ showMascot: checked })}
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                  <p className="font-medium text-gray-700 mb-1.5">Saved contact info</p>
                  <div className="space-y-1 text-gray-600">
                    <p>
                      Live link:{" "}
                      {signatureStyle.liveUrl ? (
                        <span className="text-gray-900 break-all">{signatureStyle.liveUrl}</span>
                      ) : (
                        <span className="text-amber-700">not saved</span>
                      )}
                    </p>
                    <p>
                      Phone number:{" "}
                      {signatureStyle.phoneNumber ? (
                        <span className="text-gray-900">{signatureStyle.phoneNumber}</span>
                      ) : (
                        <span className="text-amber-700">not saved</span>
                      )}
                    </p>
                  </div>
                </div>

                {signatureStyle.showDescription && (
                  <div>
                    <Label htmlFor="sigTextOverride">Description override (optional)</Label>
                    <Textarea
                      id="sigTextOverride"
                      value={signatureStyle.textOverride || ""}
                      onChange={(e) => updateSignatureStyle({ textOverride: e.target.value || null })}
                      placeholder="Leave blank to use this genie's description"
                      rows={2}
                    />
                  </div>
                )}

                <div>
                  <Label>Preview</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Phone and link reflect the saved contact info above — use Fix to refresh them.
                  </p>
                  <div className="mt-2 p-4 sm:p-6 bg-gray-50 rounded-lg overflow-x-auto">
                  <div
                    className="min-w-[320px]"
                    dangerouslySetInnerHTML={{
                      __html: renderSignaturePreviewHtml(branding, {
                        genieName,
                        layout: signatureStyle.layout,
                        showDescription: signatureStyle.showDescription,
                        showMascot: signatureStyle.showMascot,
                        showQrCode: signatureStyle.showQrCode,
                        showChannelBadges: signatureStyle.showChannelBadges,
                        descriptionText: signatureStyle.textOverride,
                        phoneNumber: signatureStyle.phoneNumber,
                        liveUrl: signatureStyle.liveUrl,
                      }),
                    }}
                  />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {onSwitchModal && (
          <TabsContent value="more" className="space-y-4">
            <Card className="relative">
              {isLocked && <LockedOverlay feature="BRANDING" />}
              <CardHeader>
                <CardTitle>More Branding Tools</CardTitle>
                <CardDescription>
                  Jump to the dedicated editors for how your genie shows up outside the app.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {MORE_BRANDING_TOOLS.map(({ modal, icon: Icon, title, description }) => (
                    <button
                      key={modal}
                      type="button"
                      onClick={() => onSwitchModal(modal)}
                      className="flex flex-col items-start gap-2 p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left"
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">{title}</span>
                      </div>
                      <p className="text-xs text-gray-500">{description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {onSave && (
        <div className="flex justify-start">
          {isLocked ? (
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white hover:opacity-90"
            >
              <Lock className="w-4 h-4 mr-2" />
              Upgrade to Unlock
            </Button>
          ) : (
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Branding"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
